import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clapperboard, Code2, FileText, Gamepad2, LineChart, Presentation, Telescope } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { showUpgradePrompt } from '../plans/UpgradePrompt';
import { useAuth } from '../auth/AuthProvider';
import { Composer, type SendPayload } from './Composer';
import { ErrorNote } from '../../components/ui';
import type { Project } from '../../types/api';

const SUGGESTIONS = [
  { icon: <Gamepad2 size={15} />, text: 'Build a polished, playable racing game' },
  { icon: <LineChart size={15} />, text: 'Create an interactive dashboard from my spreadsheet' },
  { icon: <Presentation size={15} />, text: 'Make a professional presentation about renewable energy' },
  { icon: <Telescope size={15} />, text: 'Research the current electric vehicle market and write a report' },
  { icon: <FileText size={15} />, text: 'Draft a project proposal I can export to Word' },
  { icon: <Code2 size={15} />, text: 'Build a full-stack task management app' },
  { icon: <Clapperboard size={15} />, text: 'Create a poster image and a 30-second jingle for my café' },
];

function greeting(name: string) {
  const h = new Date().getHours();
  return `${h < 5 ? 'Up late' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'}, ${name.split(' ')[0]}`;
}

export function HomePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const projectId = params.get('project');
  const project = useQuery({ queryKey: ['project-lite', projectId], queryFn: () => api<{ project: Project }>(`/api/projects/${projectId}`).then((r) => r.project), enabled: !!projectId });
  const [error, setError] = useState<unknown>(null);
  const [seed, setSeed] = useState(0);

  const start = async (p: SendPayload) => {
    setError(null);
    try {
      const { conversation } = await api<{ conversation: { id: string } }>('/api/conversations', { body: { projectId: projectId ?? null, model: p.model || undefined } });
      const r = await api<{ runId: string; assistantMessageId: string; userMessageId: string }>(`/api/conversations/${conversation.id}/messages`, { body: { requestId: globalThis.crypto?.randomUUID?.(), text: p.text, attachmentIds: p.attachmentIds, mode: p.mode, model: p.model || undefined, thinking: p.thinking, webSearch: p.webSearch } });
      sessionStorage.setItem(`lumnos.pendingRun.${conversation.id}`, JSON.stringify(r));
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['account'] });
      nav(`/chat/${conversation.id}`);
    } catch (e) { setError(e); if (e instanceof ApiError && e.code === 'out_of_credits') showUpgradePrompt(); throw e; }
  };

  return (
    <div className="home">
      <div className="home__inner">
        <h1 className="home__greeting">{project.data ? <>New chat in <em>{project.data.name}</em></> : greeting(user?.displayName ?? 'there')}</h1>
        <Composer key={seed} draftKey={`new-${projectId ?? 'home'}`} running={false} onSend={start} autoFocus placeholder="How can I help you today?" />
        <ErrorNote error={error} />
        <div className="suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s.text} className="suggestion" onClick={() => { try { localStorage.setItem(`lumnos.draft.new-${projectId ?? 'home'}`, s.text); } catch { /* ignore */ } setSeed((x) => x + 1); }}>
              {s.icon}<span>{s.text}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
